// @givmo/board — motions: the wording, who moved and seconded it, and how the board voted.
//
// ══ MOTIONS ARE FIRST-CLASS, AND THE VOTE IS THE OFFICER'S RECORD ══════════════════════════════
//
// Plan §1.7 lists votes among the signature-class acts, and the tension resolves on WHERE THE ACT
// HAPPENS: a vote taken in a room is not performed through the module, so the secretary writes it
// down. **There is no director-side voting UI in this slice, deliberately** — what rides the step-up
// rail is what a director performs THROUGH the module (the consent signature, the acknowledgment, the
// minute signature). A remote/electronic vote cast through the portal WOULD be a signature-class act,
// and is a later item; nothing here should be mistaken for its start.
//
// ══ THE THREE THINGS THE PRODUCT NEVER DECIDES ═════════════════════════════════════════════════
//
//   1. **THE OUTCOME.** Whether a motion carried is the chair's declaration. The module holds the
//      recorded threshold and states the tally beside it, and §3.2's Band 3 refuses any product-voice
//      statement that an action is required, permitted, valid or effective. So `outcome` is a field
//      the officer fills in, never a computation over the tally — and a tally that disagrees with the
//      recorded outcome is shown as what it is, because both are the organization's record.
//   2. **QUORUM.** See `quorum-block.tsx`. Arithmetic plus the organization's recorded parameter with its
//      attribution, never "quorum met". A RECUSED DIRECTOR IS STILL IN BOTH NUMBERS.
//   3. **DISINTERESTEDNESS.** Who the board determined was disinterested for a conflicted act, and the
//      basis it stated, are DATA ENTRY of the board's own determination. There is no conflict data
//      anywhere in the module from which it could be inferred, and there must never be. The backend
//      holds the limbs to all-or-none by CHECK.
//
// ══ TWO RECORDING MODES, AND WHY THE FORM REFUSES BOTH AT ONCE ═════════════════════════════════
//
// `record_vote` takes EITHER per-seat casts OR the three counts. With casts supplied the backend
// DERIVES the tally from them rather than accepting one alongside — so an organization that records five
// individual votes and a tally of six has recorded something the module refuses to store, and the wire
// model refuses both together. Many small boards record only a tally, which is why the tally mode is
// first-class rather than a fallback. The dialog makes the choice explicit before either set of fields
// appears, so the refusal is never a 422 the officer has to decode.
//
// ══ RECUSAL IS A STATE, IN ITS OWN TABLE ═══════════════════════════════════════════════════════
//
// Not a value of the vote cast. Two database triggers make a recusal and a cast mutually exclusive in
// both directions, which is what makes "its vote is uncastable" structural rather than a convention.
// The deeper reason for the separate table is §3.7: a recusal that lived on the vote row would be one
// careless COUNT away from being subtracted from the quorum denominator it must stay in. So this panel
// shows a recused seat as a LABEL beside a name and never as a number beside a quorum figure.
//
// ══ THE PERMISSION SPLIT IS WHY THERE ARE TWO VOTE ROUTES ══════════════════════════════════════
//
// An ordinary motion's vote is `board_meeting:write` (Member+). A motion that APPROVES MINUTES
// produces the organization's record, so it carries `board_record:approve` (Owner only) on its own route,
// and each route refuses the other's motion with a message naming where it belongs. Without the split
// a Member could approve the minutes by recording a vote. This panel picks the route from the motion's
// own `approves_minutes_record_id`, so the authority follows the motion rather than the button.

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  isGivmoError,
  type BoardCompensationDecisionOut,
  type OrgBoardMemberOut,
  type BoardMotionOut,
  type OrgBoardRecordOut,
  type BoardMotionOutcome,
  type BoardMotionVoteCast,
  type BoardMeetingSegmentOut,
  type BoardCommitteeOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  RowActions,
  Select,
  Switch,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { ReadOnlyDate } from '../../components/read-only-date';
import { recordDateWording } from '../records/records-decode';
import { displayDate, displayInstant } from '../../lib/display';
import { motionConflictLinks } from '../conflicts/conflicts-decode';
import { PreVoteConflictBlock } from './pre-vote-conflict-block';
// CBG-YR3-ELECT: an election's result put on the roster, the officer's own act.
import { RecordResultDialog } from './record-result-dialog';
import { extractTermSuggestions } from '../roster/terms-decode';
import { termsWords as tw } from '../roster/terms-words';
import { QuorumBlock } from './quorum-block';
import {
  approvesMinutes,
  disinterestedSentence,
  extractAttendance,
  extractCasts,
  extractLapsedTermFlags,
  extractMotions,
  extractRecusals,
  isKeptConfidential,
  isWordingFrozen,
  meetingDayOf,
  motionNamedSeatIds,
  motionOutcomeLabel,
  motionOutcomeTone,
  motionStatusLabel,
  motionStatusTone,
  recusedSeatIds,
  seatPickerLabel,
  segmentHeading,
  seatName,
  seatNames,
  seatsServingOn,
  takesTheConfidentialSwitch,
  tallySentence,
  voteCastLabel,
  voteCastTone,
  votingSeats,
  nonDirectorSeats,
  type MeetingDay,
} from './in-meeting-decode';
import { closedSessionWords as words } from './closed-session-words';
import { joinWords, termEndedBefore } from '../roster/officer-roles';
import { termNoteWords } from '../roster/term-note-words';
import { officerRolesWords as roles } from '../roster/officer-roles-words';
import { useBoardAnswersForm990, useBoardOperations } from '../../board-runtime';
import { useBoardPermission } from '../../board-host';
import type { MotionApprovalWords } from './motion-approval';
import {
  closedMinutesOfTheMeeting,
  minutesRuleWords as rc11,
  theClosedMinutesOf,
} from '../minutes/minutes-rule-words';
import {
  minutesOfTheMeeting,
  namedMeetingDate,
  theMinutesOf,
} from '../consents/minutes-consent-words';
import {
  decodeApprovableMinutes,
  presidingSeatId,
  type ApprovableMinutes,
} from './presiding-decode';
import { presidingWords as presiding } from './presiding-words';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';
import { committeeSigners, committeeVotingSeatIds } from '../committees/committees-decode';
// CBG-YR4-PAY (frame c1): a carried motion's compensation details, and the part on its card.
import { decisionsForMotion } from '../compensation/compensation-decode';
import { MotionDecisionDialog } from '../compensation/compensation-dialogs';
import {
  useMeetingCompensation,
  useRefreshCompensation,
} from '../compensation/compensation-queries';
import { MotionCompensation } from '../compensation/motion-compensation';
import { compensationWords as pay } from '../compensation/compensation-words';

/** CBG-RC11 (C5): the motion to record approves these minutes, named by public facts only. */
export interface MotionApproving {
  recordId: string;
  /** The minutes' names; a read of their meeting that failed names them as well as it can. */
  words: MotionApprovalWords;
}

const OUTCOME_OPTIONS: readonly { value: BoardMotionOutcome; label: string }[] = [
  { value: 'carried', label: 'Carried' },
  { value: 'failed', label: 'Failed' },
];

const CAST_OPTIONS: readonly { value: BoardMotionVoteCast; label: string }[] = [
  { value: 'for', label: 'For' },
  { value: 'against', label: 'Against' },
  { value: 'abstain', label: 'Abstained' },
];

export interface MotionsPanelProps {
  orgRef: string;
  meetingId: string;
  /**
   * CBG-YR6-MEET: the committee whose meeting this is (its read), or null for the board's — a vote is
   * then a voting member's of the committee, director or not.
   */
  committee?: BoardCommitteeOut | null;
  /**
   * CBG-YR6: the meeting is a committee's (its own `committee_id`), whether or not its committee's
   * read is in — a carried motion's compensation decision is then the committee's.
   */
  committeeMeeting?: boolean;
  /** The WHOLE roster, former seats included (`include_removed`): every recorded name resolves. */
  roster: readonly OrgBoardMemberOut[];
  /** The meeting's day: the motion, vote and recusal pickers offer the seats that served on it
   *  (CBG-FIX41), plus any seat a recorded motion already names. */
  servedOn: MeetingDay | null;
  segments: readonly BoardMeetingSegmentOut[];
  readOnly: boolean;
  /**
   * The MEETING's IANA zone, threaded down to the pre-vote conflict block.
   *
   * ⚠ Required, and deliberately not defaulted to the browser's zone. An officer's act recorded in the
   * room belongs to the meeting's calendar, and a zone-less render dates an evening act to the next
   * day — the exact defect the backend fixed on its own side of this data by making `time_zone` a
   * required argument rather than one with a fallback.
   */
  timeZone: string | null;
  /**
   * CBG-RC11 (C5): arriving from an earlier meeting's minutes, the motion dialog opens in its approval
   * mode for them. Offered only where this meeting's record is still open to write.
   */
  approving?: MotionApproving | null;
  /** The approval-mode dialog closed, recorded or not. */
  onApprovingClosed?: () => void;
}

export function MotionsPanel({
  orgRef,
  meetingId,
  committee = null,
  committeeMeeting = false,
  roster,
  servedOn,
  segments,
  readOnly,
  timeZone,
  approving = null,
  onApprovingClosed,
}: MotionsPanelProps) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  // A motion that approves minutes produces the organization's record, so its vote is the Owner-only
  // `board_record:approve` route (the VoteDialog below picks the route from the motion).
  const canApproveMinutes = useBoardPermission('board_record:approve');
  const [drafting, setDrafting] = useState(false);
  const [editing, setEditing] = useState<BoardMotionOut | null>(null);
  const [voting, setVoting] = useState<BoardMotionOut | null>(null);
  const [recusing, setRecusing] = useState<BoardMotionOut | null>(null);
  const [withdrawing, setWithdrawing] = useState<BoardMotionOut | null>(null);
  // CBG-YR3-ELECT (frame t5): the carried motion whose result the officer is putting on the roster.
  // A roster act (`board_member:manage`), so it stays open after this meeting's record closes.
  const canManageRoster = useBoardPermission('board_member:manage');
  const [recordingResult, setRecordingResult] = useState<BoardMotionOut | null>(null);
  // CBG-YR4-PAY (frame c1): the meeting's compensation decisions and the papers they may name — null
  // while the backend serves none (its route miss), when the card draws nothing of them. A motion's
  // details are `board_meeting:write`, the in-meeting record's own authority.
  const answersForm990 = useBoardAnswersForm990();
  const compensationQuery = useMeetingCompensation(orgRef, meetingId);
  const compensation = compensationQuery.data ?? null;
  const refreshCompensation = useRefreshCompensation(orgRef);
  const [compensating, setCompensating] = useState<{
    motion: BoardMotionOut;
    decision: BoardCompensationDecisionOut | null;
  } | null>(null);

  const motionsOptions = operations.officerListBoardMotionsOptions({
    path: { org_ref: orgRef, meeting_id: meetingId },
  });
  const motionsQuery = useQuery({ ...motionsOptions, select: extractMotions });
  const motions = motionsQuery.data ?? [];
  const names = seatNames(roster);
  const offered = seatsServingOn(roster, servedOn, motionNamedSeatIds(motions));

  // CBG-YR3-ROLES-PRESIDING: who presided, recorded in "Who was there" (the same read the attendance
  // panel makes), so an approving motion's "Who presided" starts from it.
  const attendanceQuery = useQuery({
    ...operations.officerListBoardMeetingAttendanceOptions({
      path: { org_ref: orgRef, meeting_id: meetingId },
    }),
    select: extractAttendance,
  });
  const presidingId = presidingSeatId(attendanceQuery.data ?? []);
  // CBG-FIX57: the minutes a motion here may approve — the server's list, decided by the motion's own
  // start checks (backend §P11). Read only where a motion can be recorded.
  const approvableOptions = operations.officerListBoardApprovableMinutesOptions({
    path: { org_ref: orgRef, meeting_id: meetingId },
  });
  const approvableQuery = useQuery({
    ...approvableOptions,
    enabled: !readOnly,
    select: decodeApprovableMinutes,
  });

  // CBG-YR3-ANNUAL: the items Board's suggestion added or wrote names into. A carried motion made on
  // one offers the result as a button; any other carried motion, in its menu (backend §13).
  const suggestionsQuery = useQuery({
    ...operations.officerGetBoardTermSuggestionsOptions({
      path: { org_ref: orgRef, meeting_id: meetingId },
    }),
    enabled: canManageRoster,
    select: extractTermSuggestions,
  });
  const suggestedItemIds = new Set<string>([
    ...(suggestionsQuery.data?.addedItemIds ?? []),
    ...(suggestionsQuery.data?.addedAt
      ? [
          suggestionsQuery.data.templateSections.directors,
          suggestionsQuery.data.templateSections.officers,
        ].filter((id): id is string => id !== null)
      : []),
  ]);
  const madeUnderSuggestion = (motion: BoardMotionOut) =>
    typeof motion.agenda_item_id === 'string' && suggestedItemIds.has(motion.agenda_item_id);

  // A recorded vote can make minutes the record, so the list of approvable minutes reads again too.
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: motionsOptions.queryKey }),
      queryClient.invalidateQueries({ queryKey: approvableOptions.queryKey }),
    ]);

  const removeMotion = useMutation({
    mutationFn: async (motion: BoardMotionOut) => {
      await operations.officerDeleteBoardMotion({
        path: { org_ref: orgRef, meeting_id: meetingId, motion_id: motion.id },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Motion removed.');
      void invalidate();
    },
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t remove that. Please try again.',
      );
    },
  });

  // CBG-RC12-REC: the board's choice to keep a closed session's decision out of the meeting's
  // minutes. The one field a voted motion's edit accepts, until the minutes are approved.
  const keepConfidential = useMutation({
    mutationFn: async (input: { motionId: string; kept: boolean }) => {
      await operations.officerUpdateBoardMotion({
        path: { org_ref: orgRef, meeting_id: meetingId, motion_id: input.motionId },
        body: { kept_confidential: input.kept },
        throwOnError: true,
      });
    },
    onSuccess: () => void invalidate(),
    onError: (caught) => {
      toast.error(isGivmoError(caught) ? caught.message : words.keepConfidentialFailed);
    },
  });

  const removeRecusal = useMutation({
    mutationFn: async (input: { motionId: string; seatId: string }) => {
      await operations.officerRemoveBoardMotionRecusal({
        path: {
          org_ref: orgRef,
          meeting_id: meetingId,
          motion_id: input.motionId,
          board_member_id: input.seatId,
        },
        throwOnError: true,
      });
    },
    onSuccess: () => void invalidate(),
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t remove that. Please try again.',
      );
    },
  });

  // CBG-YR4-PAY: one person's details removed from a motion, while the meeting's record is open.
  const removeDecision = useMutation({
    mutationFn: async (decision: BoardCompensationDecisionOut) => {
      await operations.officerRemoveBoardMotionCompensationDecision({
        path: {
          org_ref: orgRef,
          meeting_id: meetingId,
          motion_id: decision.decision.motion_id ?? '',
          decision_id: decision.id,
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(pay.removed);
      void refreshCompensation();
    },
    onError: (caught) => {
      toast.error(isGivmoError(caught) ? caught.message : pay.removeFailed);
    },
  });
  // Details may be entered on a motion recorded as carried, while the meeting's record is open.
  const takesCompensation = (motion: BoardMotionOut) =>
    !readOnly &&
    compensation !== null &&
    compensation.recordOpen &&
    motion.status === 'voted' &&
    motion.outcome === 'carried';

  return (
    <Card testId="motions-panel">
      <CardBody className="flex flex-col gap-16">
        <div className="flex flex-wrap items-start justify-between gap-12">
          <div className="flex flex-col gap-4">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              What the board moved
            </h2>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              Each motion in the words it was put in, who moved and seconded it, and how the board
              voted — as you record it.
            </p>
          </div>
          {!readOnly ? (
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => setDrafting(true)}
              testId="motions-add"
            >
              Record a motion
            </Button>
          ) : null}
        </div>

        {motions.length === 0 ? (
          <p
            className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
            data-testid="motions-empty"
          >
            {motionsQuery.isPending ? 'Loading…' : 'No motions recorded for this meeting yet.'}
          </p>
        ) : (
          <ul className="flex flex-col gap-12" data-testid="motions-list">
            {motions.map((motion) => (
              <MotionCard
                key={motion.id}
                motion={motion}
                names={names}
                timeZone={timeZone}
                readOnly={readOnly}
                canApproveMinutes={canApproveMinutes}
                closedSessionDecision={takesTheConfidentialSwitch(motion, segments)}
                savingConfidential={
                  keepConfidential.isPending && keepConfidential.variables?.motionId === motion.id
                }
                onKeepConfidential={(kept) =>
                  keepConfidential.mutate({ motionId: motion.id, kept })
                }
                onEdit={() => setEditing(motion)}
                onVote={() => setVoting(motion)}
                onRecuse={() => setRecusing(motion)}
                onWithdraw={() => setWithdrawing(motion)}
                onRemove={() => removeMotion.mutate(motion)}
                onRemoveRecusal={(seatId) => removeRecusal.mutate({ motionId: motion.id, seatId })}
                onAddCompensation={
                  takesCompensation(motion)
                    ? () => setCompensating({ motion, decision: null })
                    : undefined
                }
                compensation={
                  decisionsForMotion(compensation, motion.id).length > 0 ? (
                    <MotionCompensation
                      motionId={motion.id}
                      decisions={decisionsForMotion(compensation, motion.id)}
                      canChangeDetails={takesCompensation(motion)}
                      onEdit={(decision) => setCompensating({ motion, decision })}
                      onRemove={(decision) => removeDecision.mutate(decision)}
                    />
                  ) : null
                }
                resultAct={
                  canManageRoster && motion.status === 'voted' && motion.outcome === 'carried'
                    ? {
                        prominent: madeUnderSuggestion(motion),
                        onOpen: () => setRecordingResult(motion),
                      }
                    : undefined
                }
              />
            ))}
          </ul>
        )}
      </CardBody>

      {recordingResult !== null ? (
        <RecordResultDialog
          orgRef={orgRef}
          record={{
            source: { motionId: recordingResult.id },
            words: recordingResult.motion_text,
            standing: tw.carried(
              servedOn !== null ? displayDate(servedOn.day) : displayDate(recordingResult.voted_at),
            ),
            day: servedOn?.day ?? '',
          }}
          roster={roster}
          named={
            madeUnderSuggestion(recordingResult)
              ? {
                  directors: (suggestionsQuery.data?.directors ?? []).map(
                    (row) => row.board_member_id,
                  ),
                  officers: (suggestionsQuery.data?.officers ?? []).map(
                    (row) => row.board_member_id,
                  ),
                }
              : undefined
          }
          onClose={() => setRecordingResult(null)}
          onRecorded={() => setRecordingResult(null)}
        />
      ) : null}

      {approving !== null && !readOnly && !drafting && editing === null ? (
        <MotionDialog
          orgRef={orgRef}
          meetingId={meetingId}
          motion={null}
          roster={offered}
          segments={segments}
          approving={approving}
          presidingSeatId={presidingId}
          onClose={() => onApprovingClosed?.()}
          onSaved={() => {
            onApprovingClosed?.();
            void invalidate();
          }}
        />
      ) : null}

      {drafting || editing !== null ? (
        <MotionDialog
          orgRef={orgRef}
          meetingId={meetingId}
          motion={editing}
          roster={offered}
          segments={segments}
          approvable={editing === null ? (approvableQuery.data ?? []) : []}
          presidingSeatId={presidingId}
          onClose={() => {
            setDrafting(false);
            setEditing(null);
          }}
          onSaved={() => {
            setDrafting(false);
            setEditing(null);
            void invalidate();
          }}
        />
      ) : null}

      {voting !== null ? (
        <VoteDialog
          orgRef={orgRef}
          meetingId={meetingId}
          motion={voting}
          committee={committee}
          roster={offered}
          servedOn={servedOn}
          onClose={() => setVoting(null)}
          onRecorded={() => {
            setVoting(null);
            void invalidate();
          }}
        />
      ) : null}

      {compensating !== null && compensation !== null ? (
        <MotionDecisionDialog
          orgRef={orgRef}
          meetingId={meetingId}
          committee={committeeMeeting}
          motion={compensating.motion}
          meetingDay={servedOn !== null ? displayDate(servedOn.day) : null}
          papers={compensation.papers}
          decision={compensating.decision}
          answersForm990={answersForm990}
          onClose={() => setCompensating(null)}
          onSaved={() => {
            setCompensating(null);
            void refreshCompensation();
          }}
        />
      ) : null}

      {recusing !== null ? (
        <RecusalDialog
          orgRef={orgRef}
          meetingId={meetingId}
          motion={recusing}
          roster={offered}
          onClose={() => setRecusing(null)}
          onRecorded={() => {
            setRecusing(null);
            void invalidate();
          }}
        />
      ) : null}

      {withdrawing !== null ? (
        <WithdrawDialog
          orgRef={orgRef}
          meetingId={meetingId}
          motion={withdrawing}
          onClose={() => setWithdrawing(null)}
          onWithdrawn={() => {
            setWithdrawing(null);
            void invalidate();
          }}
        />
      ) : null}
    </Card>
  );
}

/**
 * CBG-RC12-REC (D-CBG-25) — the board's choice to keep a decision made in a closed session out of the
 * meeting's minutes. Off by default: a decision is reported unless the board says otherwise. A
 * dependent row under the motion, so it carries a leading rule (the givmo-design skill's rule).
 */
function KeepConfidential({
  motionId,
  kept,
  disabled,
  onChange,
}: {
  motionId: string;
  kept: boolean;
  disabled: boolean;
  onChange: (kept: boolean) => void;
}) {
  return (
    <div
      className="mt-4 flex flex-col gap-4 border-l-2 border-border-strong pl-12"
      data-testid={`motion-confidential-${motionId}`}
    >
      <label className="flex items-center gap-10">
        <Switch
          appearance="saas"
          checked={kept}
          disabled={disabled}
          onCheckedChange={onChange}
          testId={`motion-confidential-switch-${motionId}`}
        />
        <span className="font-sans text-s14 font-w500 tracking-givmo text-text-strong">
          {words.keepConfidential}
        </span>
      </label>
      <span
        className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
        data-testid={`motion-confidential-line-${motionId}`}
      >
        {kept ? words.keptConfidentialOn : words.keptConfidentialOff}
      </span>
    </div>
  );
}

function MotionCard({
  motion,
  names,
  timeZone,
  readOnly,
  canApproveMinutes,
  closedSessionDecision,
  savingConfidential,
  onKeepConfidential,
  onEdit,
  onVote,
  onRecuse,
  onWithdraw,
  onRemove,
  onRemoveRecusal,
  resultAct,
  onAddCompensation,
  compensation,
}: {
  motion: BoardMotionOut;
  names: Map<string, string>;
  timeZone: string | null;
  readOnly: boolean;
  /** Whether the person holds `board_record:approve`, the string a minutes-approval vote needs. */
  canApproveMinutes: boolean;
  /** CBG-RC12-REC: a voted decision made in an executive session, which the board may keep
   *  confidential (frame `r2-decision`); an open or withdrawn motion carries no switch. */
  closedSessionDecision: boolean;
  savingConfidential: boolean;
  onKeepConfidential: (kept: boolean) => void;
  onEdit: () => void;
  onVote: () => void;
  onRecuse: () => void;
  onWithdraw: () => void;
  onRemove: () => void;
  onRemoveRecusal: (seatId: string) => void;
  /** CBG-YR3-ELECT: "Record the result on the roster" for a carried motion — a button when the motion
   *  was made on an item Board's suggestion added, else an entry in this card's menu. */
  resultAct?: { prominent: boolean; onOpen: () => void };
  /** CBG-YR4-PAY: "Add compensation details", where this carried motion takes them (frame c1). */
  onAddCompensation?: () => void;
  /** CBG-YR4-PAY: the card's part listing the details recorded on it, in the order added. */
  compensation?: ReactNode;
}) {
  const casts = extractCasts(motion);
  const recusals = extractRecusals(motion);
  const lapsed = extractLapsedTermFlags(motion);
  const tally = tallySentence(motion);
  const disinterested = disinterestedSentence(motion);
  const outcome = motionOutcomeLabel(motion.outcome);
  const frozen = isWordingFrozen(motion);
  // Already narrowed to confirmations and already confidentiality-scoped by the backend. Read off the
  // motion row rather than fetched: the motion list carries them, so a per-motion request would be a
  // second, later answer to a question this row has already answered.
  const conflictLinks = motionConflictLinks(motion);

  // A withdrawn motion accepts no vote, no recusal and no edit — the backend refuses each, and an
  // affordance whose only outcome is a refusal the officer cannot act on is worse than its absence.
  // (It stays DELETABLE: `delete_motion` refuses only a VOTED motion.)
  const settled = frozen || motion.status === 'withdrawn';

  const actions = [
    ...(settled
      ? []
      : [
          {
            label: 'Edit this motion',
            testId: `motion-edit-${motion.id}`,
            onSelect: onEdit,
          },
          // Voting a motion that approves minutes is `board_record:approve`: without it, the act
          // is not offered.
          ...(approvesMinutes(motion) && !canApproveMinutes
            ? []
            : [
                {
                  label: 'Record the vote',
                  testId: `motion-vote-${motion.id}`,
                  onSelect: onVote,
                },
              ]),
          {
            label: 'Record a recusal',
            testId: `motion-recuse-${motion.id}`,
            onSelect: onRecuse,
          },
        ]),
    ...(motion.status === 'open'
      ? [
          {
            label: 'Withdraw it',
            testId: `motion-withdraw-${motion.id}`,
            onSelect: onWithdraw,
          },
        ]
      : []),
    ...(frozen
      ? []
      : [
          {
            label: 'Remove this motion',
            testId: `motion-remove-${motion.id}`,
            destructive: true,
            onSelect: onRemove,
          },
        ]),
  ];

  // The record's own acts close with the record; the roster's act from a carried motion does not.
  const menu = [
    ...(readOnly ? [] : actions),
    ...(onAddCompensation !== undefined
      ? [
          {
            label: pay.addDetails,
            testId: `motion-compensation-add-${motion.id}`,
            onSelect: onAddCompensation,
          },
        ]
      : []),
    ...(resultAct !== undefined && !resultAct.prominent
      ? [
          {
            label: tw.recordResult,
            testId: `motion-record-result-menu-${motion.id}`,
            onSelect: resultAct.onOpen,
          },
        ]
      : []),
  ];

  return (
    <li
      className="flex flex-col gap-10 rounded-lg border border-border-subtle p-12"
      data-testid={`motion-${motion.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-12">
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-8">
            <Badge tone={motionStatusTone(motion.status)}>{motionStatusLabel(motion.status)}</Badge>
            {outcome !== null ? (
              <Badge tone={motionOutcomeTone(motion.outcome)}>{outcome}</Badge>
            ) : null}
            {approvesMinutes(motion) ? <Badge tone="info">Approves a set of minutes</Badge> : null}
            {motion.amends_motion_id ? <Badge tone="neutral">Amends another motion</Badge> : null}
          </div>

          {/* The exact wording, verbatim and never trimmed for display. Whitespace is preserved
              because a motion is sometimes recorded with deliberate line breaks. */}
          <p
            className="whitespace-pre-wrap font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong"
            data-testid={`motion-text-${motion.id}`}
          >
            {motion.motion_text}
          </p>

          <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
            Moved by {seatName(names, motion.moved_by_board_member_id)}
            {motion.seconded_by_board_member_id
              ? ` · seconded by ${seatName(names, motion.seconded_by_board_member_id)}`
              : null}
            {motion.voted_at ? ` · voted ${displayInstant(motion.voted_at)}` : null}
          </span>

          {resultAct?.prominent ? (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              className="self-start"
              onClick={resultAct.onOpen}
              testId={`motion-record-result-${motion.id}`}
            >
              {tw.recordResult}
            </Button>
          ) : null}

          {closedSessionDecision ? (
            <KeepConfidential
              motionId={motion.id}
              kept={isKeptConfidential(motion)}
              disabled={readOnly || savingConfidential}
              onChange={onKeepConfidential}
            />
          ) : null}
        </div>

        {menu.length > 0 ? (
          <RowActions
            label="Actions for this motion"
            testId={`motion-actions-${motion.id}`}
            actions={menu}
          />
        ) : null}
      </div>

      {/* ⚠ PLACED ABOVE THE QUORUM FIGURES, THE TALLY AND THE CASTS — before the vote in reading
          order, which is the whole reason this data reaches this screen. It prompts and nothing more:
          the vote and recusal affordances above are untouched by whether it renders. */}
      <PreVoteConflictBlock
        links={conflictLinks}
        timeZone={timeZone}
        testId={`motion-conflict-links-${motion.id}`}
      />

      {frozen ? (
        <span
          className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          data-testid={`motion-frozen-${motion.id}`}
        >
          {/* Absent affordances rather than disabled ones, and a sentence saying why — the packet's
              precedent. A tally recorded against a form of words must not be able to find itself
              attached to different words, so the wording freezes at the vote, before anyone has
              approved any minutes. */}
          The wording is fixed now that a vote has been recorded against it. A change to it is
          recorded as a correction of the minutes.
        </span>
      ) : null}

      <QuorumBlock quorum={motion.quorum} testId={`motion-quorum-${motion.id}`} />

      {tally !== null ? (
        <div className="flex flex-col gap-4" data-testid={`motion-tally-${motion.id}`}>
          <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
            The vote
          </span>
          <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
            {tally}
          </span>
        </div>
      ) : null}

      {casts.length > 0 ? (
        <div className="flex flex-col gap-4" data-testid={`motion-casts-${motion.id}`}>
          <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
            How each seat voted
          </span>
          <ul className="flex flex-wrap gap-8">
            {casts.map((cast) => (
              <li key={cast.id} className="flex items-center gap-6">
                <span className="font-sans text-s12 font-w400 tracking-givmo text-text-secondary">
                  {seatName(names, cast.board_member_id)}
                </span>
                <Badge tone={voteCastTone(cast.vote_cast)}>{voteCastLabel(cast.vote_cast)}</Badge>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {recusals.length > 0 ? (
        <div className="flex flex-col gap-4" data-testid={`motion-recusals-${motion.id}`}>
          <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
            Recused
          </span>
          {/* A LABEL beside a name — never a number beside the quorum figures. A recused director is
              absent from the debate and the vote and STILL COUNTS toward quorum (DGCL § 144(d)(1)). */}
          <ul className="flex flex-col gap-4">
            {recusals.map((recusal) => (
              <li
                key={recusal.id}
                className="flex flex-wrap items-center justify-between gap-8"
                data-testid={`motion-recusal-${recusal.id}`}
              >
                <span className="font-sans text-s12 font-w400 tracking-givmo text-text-secondary">
                  {seatName(names, recusal.board_member_id)}
                  {recusal.basis_note ? ` — ${recusal.basis_note}` : null}
                </span>
                {!readOnly && !settled ? (
                  <Button
                    appearance="saas"
                    tone="secondary"
                    onClick={() => onRemoveRecusal(recusal.board_member_id)}
                    testId={`motion-recusal-remove-${recusal.id}`}
                  >
                    Remove
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
          <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            A recused seat is still counted in the figures above — recusal means they took no part
            in the debate or the vote, not that they were out of the room.
          </span>
        </div>
      ) : null}

      {disinterested !== null ? (
        <div className="flex flex-col gap-4" data-testid={`motion-disinterested-${motion.id}`}>
          <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
            The board’s determination of who was disinterested
          </span>
          <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
            {disinterested}
          </span>
          {motion.disinterested_basis ? (
            <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              Basis the board stated: {motion.disinterested_basis}
            </span>
          ) : null}
          {motion.disinterested_directors_note ? (
            <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {motion.disinterested_directors_note}
            </span>
          ) : null}
        </div>
      ) : null}

      {lapsed.length > 0 ? (
        <div className="flex flex-col gap-4" data-testid={`motion-lapsed-${motion.id}`}>
          {/* Flagged, never refused (plan §4). A seat whose recorded term has passed still voted, and
              the book records that it did — with the fact that the term had ended beside it. */}
          {lapsed.map((flag) => (
            <span
              key={flag.board_member_id}
              className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
            >
              {flag.statement}
            </span>
          ))}
        </div>
      ) : null}

      {compensation}

      {motion.withdrawal_note ? (
        <span
          className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          data-testid={`motion-withdrawal-${motion.id}`}
        >
          Withdrawn: {motion.withdrawal_note}
        </span>
      ) : null}
    </li>
  );
}

function MotionDialog({
  orgRef,
  meetingId,
  motion,
  roster,
  segments,
  approving = null,
  approvable = [],
  presidingSeatId = null,
  onClose,
  onSaved,
}: {
  orgRef: string;
  meetingId: string;
  motion: BoardMotionOut | null;
  roster: readonly OrgBoardMemberOut[];
  segments: readonly BoardMeetingSegmentOut[];
  /** CBG-RC11 (C5): a new motion that approves these minutes. Never an edit. */
  approving?: MotionApproving | null;
  /**
   * CBG-FIX57 (frames x1, x1b): the minutes a new motion here may approve, the server's list. Offered
   * as "Approves minutes (optional)" when it holds any; never on an edit or the minutes page's
   * hand-off, which already names its minutes.
   */
  approvable?: readonly ApprovableMinutes[];
  /** CBG-YR3-ROLES-PRESIDING (frame p4): the seat recorded as presiding in "Who was there". */
  presidingSeatId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const operations = useBoardOperations();
  const isEdit = motion !== null;
  // CBG-FIX57: the minutes chosen in the picker turn on RC11's approval mode in place.
  const offersMinutes = !isEdit && approving === null && approvable.length > 0;
  const [picked, setPicked] = useState<string | null>(null);
  const pickedMinutes = approvable.find((row) => row.recordId === picked) ?? null;
  const approvingNow: MotionApproving | null =
    approving ??
    (pickedMinutes !== null
      ? { recordId: pickedMinutes.recordId, words: approvableWords(pickedMinutes) }
      : null);
  // The motion's words start from the minutes' public name; the officer may reword them.
  const [motionText, setMotionText] = useState(
    approving !== null ? rc11.motionText(approving.words.inASentence) : (motion?.motion_text ?? ''),
  );
  // What the picker last wrote into the words, so a change of minutes replaces only its own prefill
  // and never words the officer typed.
  const [prefilled, setPrefilled] = useState<string | null>(null);
  // The seat that presided: required beside the link (the backend pairs them, §A9). It starts on the
  // person recorded in "Who was there" until the officer chooses; derived, so a read that arrives
  // after the dialog opened still fills it.
  const [presidedChoice, setPresidedChoice] = useState<string | null>(null);
  const [presidedTouched, setPresidedTouched] = useState(false);
  const recordedPresiding =
    presidingSeatId !== null && roster.some((seat) => seat.id === presidingSeatId)
      ? presidingSeatId
      : null;
  const presidedBy = presidedTouched ? presidedChoice : recordedPresiding;
  const [movedBy, setMovedBy] = useState<string | null>(motion?.moved_by_board_member_id ?? null);
  const [secondedBy, setSecondedBy] = useState<string | null>(
    motion?.seconded_by_board_member_id ?? null,
  );
  const [segmentId, setSegmentId] = useState<string | null>(motion?.segment_id ?? null);
  const [error, setError] = useState<string | null>(null);

  const seats = votingSeats(roster);

  const save = useMutation({
    mutationFn: async () => {
      const shared = {
        motion_text: motionText.trim(),
        moved_by_board_member_id: movedBy,
        seconded_by_board_member_id: secondedBy,
        segment_id: segmentId,
      };
      if (isEdit) {
        // ⚠ `update_motion` is a SKIP-NONE partial update (`if cmd.x is not None:` per field), so a
        // null on the wire is not "clear this" — it is "leave it alone". Sending one would look like
        // a successful save and change nothing, which is the worst of both. So the edit form never
        // produces a null for these fields (the pickers below refuse to unset in edit mode), and
        // there is deliberately no "clear" affordance: removing a recorded mover is not an operation
        // the backend has.
        await operations.officerUpdateBoardMotion({
          path: { org_ref: orgRef, meeting_id: meetingId, motion_id: motion.id },
          body: shared,
          throwOnError: true,
        });
        return;
      }
      await operations.officerCreateBoardMotion({
        path: { org_ref: orgRef, meeting_id: meetingId },
        body:
          approvingNow !== null
            ? {
                ...shared,
                approves_minutes_record_id: approvingNow.recordId,
                presided_by_board_member_id: presidedBy,
              }
            : shared,
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Motion updated.' : 'Motion recorded.');
      onSaved();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t save that. Please try again.');
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label={isEdit ? 'Edit this motion' : 'Record a motion'}
        className="flex flex-col gap-16"
        testId="motion-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {isEdit ? 'Edit this motion' : 'Record a motion'}
        </Dialog.Title>

        {offersMinutes ? (
          // CBG-FIX57 (frames x1, x1b): a held meeting's own motion chooses the minutes it approves.
          <div className="flex flex-col gap-6" data-testid="motion-approves-picker">
            <Select
              label={presiding.approvesMinutes}
              appearance="saas"
              placeholder={presiding.noMinutes}
              value={picked}
              onValueChange={(next) => {
                const record = next !== null && next !== '' ? next : null;
                const chosen = approvable.find((row) => row.recordId === record) ?? null;
                const words =
                  chosen !== null ? rc11.motionText(approvableWords(chosen).inASentence) : null;
                // Replace only what the picker wrote: words the officer typed stay theirs.
                if (motionText.trim().length === 0 || motionText === prefilled) {
                  setMotionText(words ?? '');
                  setPrefilled(words);
                }
                setPicked(record);
              }}
              options={[
                // "No minutes" takes a choice back, so the list offers it once there is one (x1b
                // draws the list before any choice: the minutes alone).
                ...(picked !== null ? [{ value: '', label: presiding.noMinutes }] : []),
                ...approvable.map((row) => ({
                  value: row.recordId,
                  label: approvableChoice(row),
                })),
              ]}
              // A closed session's choice runs long: the list wraps it inside the window rather
              // than scroll a phone's page sideways.
              popupClassName="max-w-[var(--available-width)]"
              testId="motion-approves-minutes-picker"
            />
            <FieldNote testId="motion-approves-picker-note">
              {presiding.approvesMinutesNote}
            </FieldNote>
          </div>
        ) : null}
        {offersMinutes && approvingNow !== null ? (
          <FieldNote testId="motion-approves-note">{rc11.motionApprovesNote}</FieldNote>
        ) : null}

        {approving !== null ? (
          // CBG-RC11 (C5, not drawn): what the motion approves, as a property row (RC9's dialog's).
          <div className="flex flex-col gap-6" data-testid="motion-approves">
            <div className="grid grid-cols-1 gap-4 border-t border-border-subtle pt-12 sm:grid-cols-[132px_minmax(0,1fr)] sm:gap-16">
              <span className="font-sans text-s13 font-w500 tracking-givmo text-text-tertiary">
                {rc11.motionApproves}
              </span>
              <span
                className="min-w-0 font-sans text-s14 font-w500 leading-h15 tracking-givmo text-text-strong"
                data-testid="motion-approves-minutes"
              >
                {approving.words.approves}
              </span>
            </div>
            <FieldNote testId="motion-approves-note">{rc11.motionApprovesNote}</FieldNote>
          </div>
        ) : null}

        <Textarea
          label="The motion, in the words it was put in"
          value={motionText}
          onChange={(event) => setMotionText(event.target.value)}
          rows={4}
          testId="motion-text"
        />
        <FieldNote testId="motion-text-note">
          Write it as it was moved. Once you record a vote against it, this wording is fixed — a
          tally belongs to a particular form of words.
        </FieldNote>

        <Select
          label="Moved by"
          appearance="saas"
          placeholder="Choose a seat"
          value={movedBy}
          onValueChange={(next) => (isEdit ? next !== null && setMovedBy(next) : setMovedBy(next))}
          options={seats.map((seat) => ({ value: seat.id, label: seatPickerLabel(seat) }))}
          testId="motion-moved-by"
        />
        <FieldNote testId="motion-seats-note">
          A director who has since left the board is still listed — they were in the room at the
          time, and what they did is part of this meeting's record.
        </FieldNote>

        <Select
          label="Seconded by (optional)"
          appearance="saas"
          placeholder="Choose a seat"
          value={secondedBy}
          onValueChange={(next) =>
            isEdit ? next !== null && setSecondedBy(next) : setSecondedBy(next)
          }
          options={seats.map((seat) => ({ value: seat.id, label: seatPickerLabel(seat) }))}
          testId="motion-seconded-by"
        />

        {approvingNow !== null ? (
          <div className="flex flex-col gap-6">
            {/* CBG-RC11 (C5): who presided, required beside the link — the approval is recorded
                against that person, as the note under "Approves" says. Any seat serving on the
                meeting's day may have presided. CBG-YR3-ROLES-PRESIDING (frame p4): it starts on the
                person recorded in "Who was there", which the officer changes when someone else
                presided for this vote. */}
            <Select
              label={rc11.whoPresided}
              appearance="saas"
              placeholder="Choose a seat"
              value={presidedBy}
              onValueChange={(next) => {
                setPresidedTouched(true);
                setPresidedChoice(next);
              }}
              options={roster.map((seat) => ({ value: seat.id, label: seatPickerLabel(seat) }))}
              testId="motion-presided-by"
            />
            {presidedBy !== null && presidedBy === recordedPresiding ? (
              <FieldNote testId="motion-presided-by-note">{presiding.fromWhoWasThere}</FieldNote>
            ) : null}
          </div>
        ) : null}

        {segments.length > 0 ? (
          <>
            <Select
              label="In which session (optional)"
              appearance="saas"
              placeholder="Choose a session"
              value={segmentId}
              onValueChange={(next) =>
                isEdit ? next !== null && setSegmentId(next) : setSegmentId(next)
              }
              options={segments.map((segment) => ({
                value: segment.id,
                // `segmentHeading` rather than the raw field: an untitled session would otherwise
                // render its wire enum (`executive_session`) in a picker an officer reads.
                label: segmentHeading(segment),
              }))}
              testId="motion-segment"
            />
            <FieldNote>{words.motionSessionNote}</FieldNote>
          </>
        ) : null}

        {error !== null ? <ErrorMessage testId="motion-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="motion-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={
              motionText.trim().length === 0 ||
              (approvingNow !== null && presidedBy === null) ||
              save.isPending
            }
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="motion-save"
          >
            {isEdit ? 'Save' : 'Record'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * CBG-FIX57: what the motion dialog says about minutes chosen in its picker — RC11's two namings, by
 * public facts only (backend §A7): the meeting's title and its day in its own zone, and a closed
 * session's general purpose.
 */
function approvableWords(row: ApprovableMinutes): MotionApprovalWords {
  const day = approvableDay(row);
  if (row.closedSession === null) {
    return {
      approves: theMinutesOf(row.meetingTitle, day, { keepDateTogether: true }),
      inASentence: minutesOfTheMeeting(row.meetingTitle, day),
    };
  }
  const purpose = row.closedSession.purposeLabel;
  return {
    approves: theClosedMinutesOf(row.meetingTitle, day, purpose, { keepDateTogether: true }),
    inASentence: closedMinutesOfTheMeeting(row.meetingTitle, day, purpose),
  };
}

/** The minutes' meeting's day, on that meeting's own clock (CBG-FIX41's rule). */
function approvableDay(row: ApprovableMinutes): string | null {
  return (
    meetingDayOf({ starts_at: row.meetingStartsAt, time_zone: row.meetingTimeZone })?.day ?? null
  );
}

/** A picker choice (frame x1b): "January board meeting, January 28, 2027", a closed session's by its
 *  general purpose. */
function approvableChoice(row: ApprovableMinutes): string {
  const date = namedMeetingDate(approvableDay(row), { keepDateTogether: true });
  const title = (row.meetingTitle ?? '').trim();
  const meeting =
    date !== null && title.length > 0 ? presiding.choice(title, date) : title || date || '';
  return row.closedSession === null
    ? meeting
    : presiding.closedChoice(meeting, row.closedSession.purposeLabel);
}

/** Which of the two recording modes the officer chose. See the file header. */
type VoteMode = 'seats' | 'tally';

function VoteDialog({
  orgRef,
  meetingId,
  motion,
  committee,
  roster,
  servedOn,
  onClose,
  onRecorded,
}: {
  orgRef: string;
  meetingId: string;
  motion: BoardMotionOut;
  committee: BoardCommitteeOut | null;
  roster: readonly OrgBoardMemberOut[];
  /** The meeting's day, on its own clock: the day a director's recorded term end is read against. */
  servedOn: MeetingDay | null;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const operations = useBoardOperations();
  const [mode, setMode] = useState<VoteMode>('seats');
  const [outcome, setOutcome] = useState<BoardMotionOutcome>('carried');
  const [casts, setCasts] = useState<Record<string, BoardMotionVoteCast | ''>>({});
  const [votesFor, setVotesFor] = useState('');
  const [votesAgainst, setVotesAgainst] = useState('');
  const [votesAbstaining, setVotesAbstaining] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recused = recusedSeatIds(motion);
  // A recused seat's vote is uncastable — two database triggers make a recusal and a cast mutually
  // exclusive in both directions. Offering the field would produce a refusal the officer cannot act
  // on, so the seat is listed as recused instead. It is STILL COUNTED in the quorum figures; this is
  // about which fields the form offers, not about the arithmetic.
  // CBG-YR6-MEET: at a committee's meeting, the seats recorded as voting on the committee (an ended
  // membership included, as the backend's gate admits it), director or not; its members recorded as
  // not voting are the ones named once below the fields.
  const committeeVoters = committee === null ? null : committeeVotingSeatIds(committee);
  const seats =
    committeeVoters === null
      ? votingSeats(roster)
      : roster.filter((seat) => committeeVoters.has(seat.id));
  const castable = seats.filter((seat) => !recused.has(seat.id));
  // Serving people who aren't directors have no vote: named once below the fields (CBG-YR3-ROLES).
  const nonDirectors = committee === null ? nonDirectorSeats(roster) : [];
  const notVotingOnCommittee =
    committee === null
      ? []
      : committeeSigners(committee).notVoting.map((member) => member.display_name ?? '');
  // CBG-YR3-VOTENOTE (frame t6): a director whose recorded term end is before the meeting's day is
  // flagged before the vote, in the consent page's shipped words, and NOTHING else changes: the
  // field stays enabled and the vote is recorded like any other. The backend captures the same fact
  // at the cast against the same day (§V3), so the motion card and new minutes say it afterwards.
  const lapsed = castable.flatMap((seat) => {
    const ended = termEndedBefore(seat.term_end_on, servedOn?.day ?? null);
    return ended === null ? [] : [{ seat, ended }];
  });
  const lapsedBySeat = new Map(lapsed.map(({ seat, ended }) => [seat.id, ended]));

  // What each mode needs before there is anything to send. The backend refuses an EMPTY `casts` and
  // a tally with no counts, so a save armed on neither produces a 422 naming a wire field — which is
  // exactly the failure this dialog's own header says it exists to prevent. Stated here rather than
  // caught after the round trip.
  const enteredCasts = castable.filter((seat) => casts[seat.id]).length;
  const tallyComplete =
    toCount(votesFor) !== undefined &&
    toCount(votesAgainst) !== undefined &&
    toCount(votesAbstaining) !== undefined;
  const canRecord = mode === 'seats' ? enteredCasts > 0 : tallyComplete;

  // CBG-FIX6 — a carried vote on this motion makes the minutes it names the record, and a record is
  // approved with its date, so the dialog shows that date under the same words the minutes' own
  // approval uses ("Meeting date"). Read off the record itself (the minutes take their meeting's day
  // when their draft opens); a read that fails or carries no date shows no date line, and the
  // backend's own refusal speaks if it comes to that.
  const minutesRecordId = approvesMinutes(motion) ? (motion.approves_minutes_record_id ?? '') : '';
  const approvedMinutesQuery = useQuery({
    queryKey: ['board-record', orgRef, minutesRecordId],
    enabled: minutesRecordId.length > 0,
    retry: false,
    queryFn: async (): Promise<OrgBoardRecordOut> => {
      const { data } = await operations.officerGetBoardRecord({
        path: { org_ref: orgRef, record_id: minutesRecordId },
        throwOnError: true,
      });
      return data;
    },
  });
  const approvedMinutesDate = (approvedMinutesQuery.data?.occurred_on ?? '').trim();

  const record = useMutation({
    mutationFn: async () => {
      const body =
        mode === 'seats'
          ? {
              outcome,
              casts: castable
                .filter((seat) => casts[seat.id])
                .map((seat) => ({
                  board_member_id: seat.id,
                  vote_cast: casts[seat.id] as BoardMotionVoteCast,
                })),
            }
          : {
              outcome,
              votes_for: toCount(votesFor),
              votes_against: toCount(votesAgainst),
              votes_abstaining: toCount(votesAbstaining),
            };

      // The permission split: a motion that approves minutes produces the organization's record, so its
      // vote rides the Owner-only route. Chosen from the MOTION rather than from which button was
      // pressed, so the authority follows what is being voted on.
      if (approvesMinutes(motion)) {
        await operations.officerRecordBoardMinutesApprovalVote({
          path: { org_ref: orgRef, meeting_id: meetingId, motion_id: motion.id },
          body,
          throwOnError: true,
        });
        return;
      }
      await operations.officerRecordBoardMotionVote({
        path: { org_ref: orgRef, meeting_id: meetingId, motion_id: motion.id },
        body,
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Vote recorded.');
      onRecorded();
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record the vote. Please try again.',
      );
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !record.isPending && onClose()}>
      <Dialog.Content
        aria-label="Record the vote"
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId="vote-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Record the vote
        </Dialog.Title>

        {approvesMinutes(motion) ? (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            data-testid="vote-approves-minutes"
          >
            This motion approves a set of minutes. Recording it as carried makes those minutes the
            board’s record, and names the presiding seat as having approved them.
          </p>
        ) : null}
        {approvesMinutes(motion) && approvedMinutesDate.length > 0 ? (
          <ReadOnlyDate
            label={recordDateWording('minutes').label}
            date={approvedMinutesDate}
            testId="vote-approves-minutes-date"
          />
        ) : null}

        <Select
          label="What the chair declared"
          appearance="saas"
          value={outcome}
          onValueChange={(next) => next !== null && setOutcome(next as BoardMotionOutcome)}
          options={OUTCOME_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
          testId="vote-outcome"
        />
        <FieldNote testId="vote-outcome-note">
          The outcome is what was declared in the room, recorded as you enter it. It is not worked
          out from the numbers below.
        </FieldNote>

        <Select
          label="How the vote was taken down"
          appearance="saas"
          value={mode}
          onValueChange={(next) => next !== null && setMode(next as VoteMode)}
          options={[
            { value: 'seats', label: 'Seat by seat' },
            { value: 'tally', label: 'A tally only' },
          ]}
          testId="vote-mode"
        />
        <FieldNote testId="vote-mode-note">
          {/* Made explicit BEFORE either set of fields appears: the backend refuses both together, and
              a 422 naming `casts` is not something an officer can act on. */}
          One or the other, not both. Recording seat by seat works the totals out from the seats;
          recording a tally stores just the three numbers.
        </FieldNote>

        {mode === 'seats' ? (
          <div className="flex flex-col gap-8" data-testid="vote-seats">
            {castable.map((seat) => {
              const ended = lapsedBySeat.get(seat.id) ?? null;
              const select = (
                <Select
                  key={seat.id}
                  label={seatPickerLabel(seat)}
                  appearance="saas"
                  placeholder="Did not vote"
                  value={casts[seat.id] || null}
                  onValueChange={(next) =>
                    setCasts((previous) => ({
                      ...previous,
                      [seat.id]: (next ?? '') as BoardMotionVoteCast | '',
                    }))
                  }
                  options={CAST_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  testId={`vote-cast-${seat.id}`}
                />
              );
              if (ended === null) return select;
              return (
                <div key={seat.id} className="flex flex-col gap-4">
                  {select}
                  <p
                    className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                    data-testid={`vote-term-note-${seat.id}`}
                  >
                    {termNoteWords.termYouRecordedEnded(displayDate(ended))}
                  </p>
                </div>
              );
            })}
            {nonDirectors.length > 0 ? (
              <p
                className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                data-testid="vote-nondirector-note"
              >
                {roles.voteNotDirectors(
                  joinWords(nonDirectors.map((seat) => seat.display_name)),
                  nonDirectors.length,
                )}
              </p>
            ) : null}
            {notVotingOnCommittee.length > 0 ? (
              <p
                className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                data-testid="vote-not-voting-on-committee-note"
              >
                {cm.voteNotOnCommittee(
                  joinWords(notVotingOnCommittee),
                  notVotingOnCommittee.length,
                )}
              </p>
            ) : null}
            {recused.size > 0 ? (
              <p
                className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                data-testid="vote-recused-note"
              >
                {recused.size === 1 ? 'One seat has' : `${recused.size} seats have`} a recusal
                recorded on this motion, so there is no vote to enter for them. They are still
                counted in the quorum figures.
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-8" data-testid="vote-tally">
            {/* CBG-YR3-VOTENOTE: no field names a person in a tally, so each held-over director is
                named once above the numbers. A tally captures nothing at the cast (§V13 V-C3). */}
            {lapsed.length > 0 ? (
              // Their own group, so a line never reads as the caption of the "For" field below it.
              <div className="flex flex-col gap-4 pb-8">
                {lapsed.map(({ seat, ended }) => (
                  <p
                    key={seat.id}
                    className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                    data-testid={`vote-tally-term-note-${seat.id}`}
                  >
                    {termNoteWords.tallyTermEnded(seat.display_name, displayDate(ended))}
                  </p>
                ))}
              </div>
            ) : null}
            <TextField
              label="For"
              type="number"
              value={votesFor}
              onChange={(event) => setVotesFor(event.target.value)}
              testId="vote-for"
            />
            <TextField
              label="Against"
              type="number"
              value={votesAgainst}
              onChange={(event) => setVotesAgainst(event.target.value)}
              testId="vote-against"
            />
            <TextField
              label="Abstaining"
              type="number"
              value={votesAbstaining}
              onChange={(event) => setVotesAbstaining(event.target.value)}
              testId="vote-abstaining"
            />
          </div>
        )}

        {error !== null ? <ErrorMessage testId="vote-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={record.isPending}
            onClick={onClose}
            testId="vote-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={record.isPending}
            disabled={!canRecord || record.isPending}
            onClick={() => {
              setError(null);
              record.mutate();
            }}
            testId="vote-save"
          >
            Record the vote
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function RecusalDialog({
  orgRef,
  meetingId,
  motion,
  roster,
  onClose,
  onRecorded,
}: {
  orgRef: string;
  meetingId: string;
  motion: BoardMotionOut;
  roster: readonly OrgBoardMemberOut[];
  onClose: () => void;
  onRecorded: () => void;
}) {
  const operations = useBoardOperations();
  const [seatId, setSeatId] = useState<string | null>(null);
  const [basisNote, setBasisNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const already = recusedSeatIds(motion);
  const seats = votingSeats(roster).filter((seat) => !already.has(seat.id));

  const record = useMutation({
    mutationFn: async () => {
      if (seatId === null) return;
      await operations.officerRecordBoardMotionRecusal({
        path: { org_ref: orgRef, meeting_id: meetingId, motion_id: motion.id },
        body: { board_member_id: seatId, basis_note: basisNote.trim() || null },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Recusal recorded.');
      onRecorded();
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      );
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !record.isPending && onClose()}>
      <Dialog.Content
        aria-label="Record a recusal"
        className="flex flex-col gap-16"
        testId="recusal-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Record a recusal
        </Dialog.Title>

        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {/* The one sentence that keeps a reader from expecting the quorum figures to move. It is
              the DGCL § 144(d)(1) rule stated as what the product does, without citing it at them. */}
          A recused seat takes no part in the debate or the vote on this motion. It is still counted
          toward the number of directors present.
        </p>

        <Select
          label="Which seat"
          appearance="saas"
          placeholder="Choose a seat"
          value={seatId}
          onValueChange={(next) => setSeatId(next)}
          options={seats.map((seat) => ({ value: seat.id, label: seatPickerLabel(seat) }))}
          testId="recusal-seat"
        />

        <Textarea
          label="The basis the board recorded (optional)"
          value={basisNote}
          onChange={(event) => setBasisNote(event.target.value)}
          rows={3}
          testId="recusal-basis"
        />

        {error !== null ? <ErrorMessage testId="recusal-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={record.isPending}
            onClick={onClose}
            testId="recusal-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={record.isPending}
            disabled={seatId === null || record.isPending}
            onClick={() => {
              setError(null);
              record.mutate();
            }}
            testId="recusal-save"
          >
            Record
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function WithdrawDialog({
  orgRef,
  meetingId,
  motion,
  onClose,
  onWithdrawn,
}: {
  orgRef: string;
  meetingId: string;
  motion: BoardMotionOut;
  onClose: () => void;
  onWithdrawn: () => void;
}) {
  const operations = useBoardOperations();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const withdraw = useMutation({
    mutationFn: async () => {
      await operations.officerWithdrawBoardMotion({
        path: { org_ref: orgRef, meeting_id: meetingId, motion_id: motion.id },
        body: { note: note.trim() || null },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Recorded as withdrawn.');
      onWithdrawn();
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      );
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !withdraw.isPending && onClose()}>
      <Dialog.Content
        aria-label="Withdraw this motion"
        className="flex flex-col gap-16"
        testId="withdraw-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Record this motion as withdrawn?
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {/* Says what withdrawing does, and nothing about what can happen afterwards. The earlier
              copy claimed "a motion the board made is not removed from the book", which is not true
              of this module: `delete_motion` refuses only a VOTED motion, so a withdrawn one can
              still be removed. Stating a guarantee the backend does not make is worse than stating
              nothing. */}
          The motion stays in the record, marked as withdrawn, with your note and the date it was
          recorded.
        </p>

        <TextField
          label="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          testId="withdraw-note"
        />

        {error !== null ? <ErrorMessage testId="withdraw-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={withdraw.isPending}
            onClick={onClose}
            testId="withdraw-cancel"
          >
            Keep it open
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={withdraw.isPending}
            onClick={() => {
              setError(null);
              withdraw.mutate();
            }}
            testId="withdraw-confirm"
          >
            Record as withdrawn
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** A count field's text as a number, or `undefined` when the officer left it empty. */
function toCount(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}
